import { useEffect, useState } from 'react';

// Engine delle mappe della piattaforma Outdoora (sito React).
//
// Provider e chiave NON sono hardcodati e NON sono variabili di build: Django
// li inietta a runtime in index.html (`window.MAP_OUTDOORA_PLATFORM_*`, vedi
// serve_index_html in padbike_admin/urls.py) a partire dalle config var
// omonime. Cosi' si cambia provider con un `heroku config:set` + restart,
// senza ricompilare il bundle.
//
// ATTENZIONE: questo modulo vale SOLO per il sito pubblico. Il tour builder
// dell'admin (customer/static/js/map.js) e' una superficie separata, resta
// sempre su Mapbox e usa la sua chiave (MAPBOX_TOUR_BUILDER_API_KEY): li'
// servono Directions, geocoder e tile di altimetria, che sono specifici di
// Mapbox. Le due chiavi sono separate proprio per poter leggere i consumi
// distinti nelle dashboard dei provider.

// Valori che restano in index.html quando NON sono passati da Django, cioe'
// solo in sviluppo con `npm start` (che serve public/index.html senza
// sostituzioni).
const ENGINE_PLACEHOLDER = '__MAP_OUTDOORA_PLATFORM_ENGINE__';
const KEY_PLACEHOLDER = '__MAP_OUTDOORA_PLATFORM_API_KEY__';

export const DEFAULT_ENGINE = 'mapbox';

function injected(windowValue, placeholder, envValue) {
  if (windowValue && windowValue !== placeholder) return windowValue;
  return envValue || '';
}

export function getMapEngine() {
  const value = injected(
    typeof window !== 'undefined' ? window.MAP_OUTDOORA_PLATFORM_ENGINE : '',
    ENGINE_PLACEHOLDER,
    process.env.REACT_APP_MAP_OUTDOORA_PLATFORM_ENGINE
  ).trim().toLowerCase();

  if (value && !ENGINES[value]) {
    console.error(
      `MAP_OUTDOORA_PLATFORM_ENGINE="${value}" non riconosciuto. ` +
      `Engine disponibili: ${Object.keys(ENGINES).join(', ')}. Uso "${DEFAULT_ENGINE}".`
    );
    return DEFAULT_ENGINE;
  }
  return value || DEFAULT_ENGINE;
}

export function getMapApiKey() {
  return injected(
    typeof window !== 'undefined' ? window.MAP_OUTDOORA_PLATFORM_API_KEY : '',
    KEY_PLACEHOLDER,
    process.env.REACT_APP_MAP_OUTDOORA_PLATFORM_API_KEY
  );
}

// `referrerPolicy` e' necessaria su TUTTI gli engine con chiave ristretta per
// dominio. Django manda `Referrer-Policy: same-origin` su ogni risposta
// (default di SecurityMiddleware), quindi senza questo override il browser non
// manderebbe nessun Referer al provider — e sia Mapbox sia Google basano le
// restrizioni per URL proprio sul Referer: risultato, 403 su ogni tile. Qui la
// policy viene sovrascritta per le sole immagini delle tile: viene inviata solo
// l'ORIGINE (mai il path), che e' quanto basta a riconoscere il dominio.
const REFERRER_POLICY = 'strict-origin-when-cross-origin';

const ENGINES = {
  mapbox: {
    requiresKey: true,
    attribution:
      '&copy; <a href="https://www.mapbox.com/about/maps">MapBox</a> contributors',
    // Tile da 512px: vedi il commento su `options`. @2x = 1024px fisici resi su
    // 512 CSS px, cioe' nitidezza retina.
    tileUrl: (key) =>
      'https://api.mapbox.com/styles/v1/padbike/cm827xfw400wh01s51if35c78/tiles/512/{z}/{x}/{y}@2x' +
      `?access_token=${key}`,
    // tileSize/zoomOffset sono OBBLIGATORI con l'URL /tiles/512/. Il tile 512
    // di Mapbox copre la STESSA area del 256 con lo stesso {z}/{x}/{y}: cambia
    // solo la densita' di pixel. Quindi il solo URL 512 non ridurrebbe le
    // richieste, perche' Leaflet di default assume tile da 256px e chiederebbe
    // esattamente gli stessi tile. Dicendogli invece che sono da 512 CSS px,
    // gli serve un livello di zoom in meno (zoomOffset -1) per coprire la
    // stessa area: circa un terzo dei tile a parita' di schermo e dettaglio.
    options: {
      tileSize: 512,
      zoomOffset: -1,
      maxZoom: 22,
      referrerPolicy: REFERRER_POLICY,
    },
  },

  openstreetmap: {
    // Tile pubbliche di openstreetmap.org: nessuna chiave.
    requiresKey: false,
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    tileUrl: () => 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: {
      tileSize: 256,
      zoomOffset: 0,
      // I server OSM pubblici non vanno oltre z19 (Mapbox arriva a 22).
      maxZoom: 19,
      referrerPolicy: REFERRER_POLICY,
    },
  },

  google: {
    requiresKey: true,
    attribution:
      '&copy; <a href="https://www.google.com/help/terms_maps/">Google</a>',
    // Map Tiles API: le tile non sono raggiungibili con la sola chiave, serve
    // prima un "session token" (vedi createGoogleSession). Per questo l'URL si
    // costruisce solo dopo la risoluzione asincrona in resolveMapLayer().
    needsSession: true,
    tileUrl: (key, session) =>
      `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${session}&key=${key}`,
    options: {
      tileSize: 256,
      zoomOffset: 0,
      maxZoom: 22,
      referrerPolicy: REFERRER_POLICY,
    },
  },
};

export const AVAILABLE_ENGINES = Object.keys(ENGINES);

// Il session token di Google vale settimane, ma qui basta tenerlo per la durata
// della pagina: una sola chiamata per caricamento invece di una per mappa.
let googleSessionPromise = null;

function createGoogleSession(key) {
  if (!googleSessionPromise) {
    googleSessionPromise = fetch(
      `https://tile.googleapis.com/v1/createSession?key=${encodeURIComponent(key)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mapType: 'roadmap',
          language: 'it-IT',
          region: 'IT',
        }),
      }
    )
      .then((r) => {
        if (!r.ok) throw new Error(`createSession HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => d.session)
      .catch((e) => {
        console.error('Google Map Tiles: creazione sessione fallita.', e);
        googleSessionPromise = null; // così un retry successivo può riprovare
        return null;
      });
  }
  return googleSessionPromise;
}

/**
 * Zoom massimo dell'engine attivo. È SEMPRE disponibile in modo sincrono, anche
 * quando il layer non è ancora risolto.
 *
 * Serve a `MapContainer`: Leaflet normalmente deduce il maxZoom della mappa da
 * quello del TileLayer, e senza un maxZoom il MarkerClusterGroup va in errore
 * ("Map has no maxZoom specified") e la mappa non viene montata affatto. Dato
 * che con Google il layer arriva in un secondo momento (serve il session
 * token), il maxZoom va passato esplicitamente alla mappa.
 */
export function getMapMaxZoom() {
  return ENGINES[getMapEngine()].options.maxZoom;
}

/**
 * Config del layer di tile per l'engine attivo, in modo SINCRONO.
 * Restituisce { url, attribution, options }, oppure null se serve una
 * risoluzione asincrona (Google) o se manca la chiave richiesta.
 */
export function resolveMapLayerSync() {
  const name = getMapEngine();
  const engine = ENGINES[name];
  const key = getMapApiKey();

  if (engine.requiresKey && !key) {
    console.error(
      `MAP_OUTDOORA_PLATFORM_API_KEY non impostata: l'engine "${name}" la richiede, la mappa resterà vuota.`
    );
    return null;
  }
  if (engine.needsSession) return null;

  return {
    engine: name,
    url: engine.tileUrl(key, null),
    attribution: engine.attribution,
    options: engine.options,
  };
}

/**
 * Come resolveMapLayerSync(), ma risolve anche gli engine che richiedono una
 * negoziazione asincrona (Google). Restituisce null se l'engine non è
 * utilizzabile: in quel caso chi chiama non deve montare il TileLayer.
 */
export async function resolveMapLayer() {
  const name = getMapEngine();
  const engine = ENGINES[name];
  const key = getMapApiKey();

  if (!engine.needsSession) return resolveMapLayerSync();
  if (engine.requiresKey && !key) {
    console.error(
      `MAP_OUTDOORA_PLATFORM_API_KEY non impostata: l'engine "${name}" la richiede, la mappa resterà vuota.`
    );
    return null;
  }

  const session = await createGoogleSession(key);
  if (!session) return null;

  return {
    engine: name,
    url: engine.tileUrl(key, session),
    attribution: engine.attribution,
    options: engine.options,
  };
}

/**
 * Versione hook, per i componenti.
 *
 * Mapbox e OpenStreetMap sono disponibili GIÀ al primo render (stato iniziale
 * sincrono): è importante, perché un render iniziale senza TileLayer lascia la
 * mappa senza maxZoom. Solo Google resta null per il tempo di ottenere il
 * session token — per quel caso `MapContainer` deve ricevere comunque un
 * maxZoom esplicito, vedi getMapMaxZoom().
 */
export function useMapLayer() {
  const [layer, setLayer] = useState(resolveMapLayerSync);

  useEffect(() => {
    if (layer) return undefined;
    let alive = true;
    resolveMapLayer().then((l) => {
      if (alive) setLayer(l);
    });
    return () => {
      alive = false;
    };
  }, [layer]);

  return layer;
}
